import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardTitle,
    IonCheckbox,
    IonCol,
    IonItem,
    IonLabel,
    IonRow,
} from "@ionic/react";
import React, { useEffect, useState } from "react";
import ErrorAlert from "../Errors/ErrorAlert";
import SuccessBanner from "../Banners/SuccessBanner";
import { BarnPerson, FullRole } from "../../interfaces/Person";
import Spinner from "../Spinners/Spinner";
import moment from "moment";
import BasicModal from "../Modals/BasicModal";
import { updatePersonRoles } from "../../utilities/barn/BarnPerson";

interface _Props {
    hasAdminPermissions: boolean;
    barnPerson: BarnPerson
    onSubmit: Function
}

const EditPersonRolesForm: React.FC<_Props> = ({hasAdminPermissions, barnPerson, onSubmit}) => {    

    const [fullRoles, setFullRoles] = useState<FullRole[] | null>(null);
    const [isRider, setIsRider] = useState(false);
    const [isOwner, setIsOwner] = useState(false);
    const [isTrainer, setIsTrainer] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [isAddRoleModalOpen, setIsAddRoleModalOpen] = useState(false);
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");

    const getData = (bp: BarnPerson) => {
        if (bp) {
            setFullRoles(bp.fullRoles || null);
            setIsRider(bp.fullRoles?.some(fr => fr.role === "rider") || false);
            setIsOwner(bp.fullRoles?.some(fr => fr.role === "owner") || false);
            setIsTrainer(bp.fullRoles?.some(fr => fr.role === "trainer") || false);
        } else {
            clearForm();
        }
    }

    const clearForm = () => {
        setFullRoles(null);
    }

    useEffect(() => {
        if (barnPerson) {
            getData(barnPerson);
        } else {
            clearForm();
        }
    }, [barnPerson]);

    const getName = (fullRole: FullRole) => {
        let name = "none";
        if (fullRole.role === "rider") {
            name = fullRole.rider?.name || "";
        } else if (fullRole.role === "owner") {
            name = fullRole.owner?.name || "";
        } else if (fullRole.role === "trainer") {
            name = fullRole.trainer?.name || "";
        }
        return name;
    }

    const getCreatedOn = (fullRole: FullRole) => {
        let createdOn = "";
        if (fullRole.role === "rider") {
            createdOn = fullRole.rider?.createdOn || "";
        } else if (fullRole.role === "owner") {
            createdOn = fullRole.owner?.createdOn || "";
        } else if (fullRole.role === "trainer") {
            createdOn = fullRole.trainer?.createdOn || "";
        }
        const formattedCreatedOn = moment(createdOn).format("MM/DD/YYYY");
        return formattedCreatedOn;
    }

    const getLocation = (fullRole: FullRole) => {
        let location = "none";
        if (fullRole.role === "rider") {
            location = fullRole.rider?.location || "";
        } else if (fullRole.role === "owner") {
            location = fullRole.owner?.location || "";
        } else if (fullRole.role === "trainer") {
            location = fullRole.trainer?.location || "";
        }
        return location;
    }

    const handleAddRole = async () => {
        setIsAddRoleModalOpen(true);
    }

    const handleCloseAddRoleModal = () => {
        setIsAddRoleModalOpen(false);
    }

    const handleEditPerson = async () => {
        setIsLoading(true);
        setIsAddRoleModalOpen(false);
        const roleResult = await updatePersonRoles(barnPerson, isRider, isOwner, isTrainer);
        if (roleResult.isSuccess) {
            setSuccess("Successfully updated the roles.");
        } else {
            setError(roleResult.message);
        }
        setIsLoading(false);
        onSubmit();
    }


    return (
        <>
            {hasAdminPermissions && (
                <IonRow className="ion-justify-content-right">
                    <IonCol className="ion-text-right">
                        <IonButton color="primary" onClick={handleAddRole}>Edit Roles</IonButton>
                    </IonCol>
                </IonRow>
            )}
            <hr/>
            <p>Role options include: Rider, Owner and Trainer.</p>
            <p>A person can have multiple roles (or even none of these roles).</p>
            <p>When a person has a role, that role can be used to enter them into a horse show or other event.</p>
            <hr/>
            {isLoading ?
                <Spinner />
                :
                <>
                    {success && <SuccessBanner success={success} />}
                    {error && <ErrorAlert width="12" error={error}/>}
                    {(fullRoles && fullRoles.length > 0) ?
                        <>
                            {fullRoles.map((fullRole, i) => (
                                <IonRow key={i}>
                                    <IonCol>
                                        <IonCard>
                                            <IonCardTitle className="m-3">
                                                <IonRow>
                                                    <IonCol sizeXs="12" sizeMd="8">{fullRole.role}</IonCol>
                                                </IonRow>
                                            </IonCardTitle>
                                            <IonCardContent>
                                                <IonRow>
                                                    <IonCol sizeXs="12" sizeMd="4">
                                                        <h3>Name</h3>
                                                        <p>{getName(fullRole)}</p>
                                                    </IonCol>
                                                    <IonCol sizeXs="12" sizeMd="4">
                                                        <h3>Created On</h3>
                                                        <p>{getCreatedOn(fullRole)}</p>
                                                    </IonCol>
                                                    <IonCol sizeXs="12" sizeMd="4">
                                                        <h3>Location</h3>
                                                        <p>{getLocation(fullRole)}</p>
                                                    </IonCol>
                                                </IonRow>                              
                                            </IonCardContent>
                                        </IonCard>
                                    </IonCol>
                                </IonRow>
                            ))}
                        </>
                        :
                        <p>No roles found.</p>
                    }
                </>
            }
            <BasicModal
                title="Add Role"
                isOpen={isAddRoleModalOpen} 
                onClose={handleCloseAddRoleModal}
                footerButtons={[
                    {
                        text: "Cancel",
                        color: "light",
                        onClick: handleCloseAddRoleModal,
                    },
                    {
                        text: "Save",
                        color: "success",
                        onClick: handleEditPerson,
                    }
                ]}
            >
                <IonRow className="ion-justify-content-center">
                    <IonCol size="12" className="ion-text-center">
                        <p>Select any roles you want to add to the person.</p>
                    </IonCol>
                </IonRow>
                <IonRow>
                    <IonCol size="12">
                        <IonItem>
                            <IonLabel>Rider</IonLabel>
                            <IonCheckbox slot="start" value="rider" checked={isRider} onIonChange={e => setIsRider(e.detail.checked)} />
                        </IonItem>
                    </IonCol>
                </IonRow>
                <IonRow>
                    <IonCol size="12">
                        <IonItem>
                            <IonLabel>Owner</IonLabel>
                            <IonCheckbox slot="start" value="owner" checked={isOwner} onIonChange={e => setIsOwner(e.detail.checked)} />
                        </IonItem>
                    </IonCol>
                </IonRow>
                <IonRow>
                    <IonCol size="12">
                        <IonItem>
                            <IonLabel>Trainer</IonLabel>
                            <IonCheckbox slot="start" value="trainer" checked={isTrainer} onIonChange={e => setIsTrainer(e.detail.checked)} />
                        </IonItem>
                    </IonCol>
                </IonRow>
            </BasicModal>
        </>
    );
};

export default EditPersonRolesForm;